import ChartDataLabels from 'chartjs-plugin-datalabels'

import { DataPoint } from 'components/chart-v2/model'
import { getOptimalColor } from 'components/chart-v2/utils'
import { ALIAS_FOR_NULL_VALUES } from 'src/constants/filter-values'

interface BarChartConfig {
  primaryDomain?: string
  availableMetrics?: Array<{ label: string; key: string; index: number }>
  selectedMetricIndex?: number
  allData?: DataPoint[] // Add this to get full dataset for scale
}

const configureBarChart = (
  data: DataPoint[],
  config: BarChartConfig = {},
  colorMap: Record<string, string>,
): {
  type: 'bar'
  data: { labels: string[]; datasets: Record<string, unknown>[] }
  options: Record<string, unknown>
  plugins: unknown[]
} => {
  const {
    primaryDomain = 'volume',
    selectedMetricIndex = 0,
    availableMetrics = [],
    allData = data, // Use allData if provided, otherwise fall back to current page data
  } = config

  // Calculate max value from ALL data for consistent scaling
  const allValues = allData.map((item) => item.values[selectedMetricIndex] ?? 0)
  const maxValue = Math.max(...allValues)
  const minValue = Math.min(...allValues)

  // Don't sort here since data is already sorted and paginated
  const valueIndex = selectedMetricIndex
  const labels = data.map((item) => item.category[0] ?? '')
  const values = data.map((item) => item.values[valueIndex] ?? 0)

  const backgroundColors = data.map((item) =>
    getOptimalColor(item.category[0] ?? '', colorMap),
  )
  const borderColors = backgroundColors // Use same colors for borders

  // Fixed height since we now limit to 10 items max

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend for single dataset
      },
      title: {
        display: false,
        text: `${availableMetrics.find((m) => m.key === primaryDomain)?.label || primaryDomain} by Brand`,
      },
    },
    indexAxis: 'y' as const,
  }

  return {
    type: 'bar' as const,
    data: {
      labels,
      datasets: [
        {
          label: 'Values',
          data: values,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1,
          borderRadius: 8,
          barThickness: 25, // Fixed thickness since we always show max 10 bars
          categoryPercentage: 0.6, // Reduce category width to increase spacing between bars
          barPercentage: 0.7, // Reduce bar width within category
        },
      ],
    },
    options: {
      ...baseOptions,
      plugins: {
        ...baseOptions.plugins,
        tooltip: {
          callbacks: {
            title: function (context: { label: string }[]) {
              return context[0]?.label ?? ''
            },
            label: function (context: { dataIndex: number }) {
              const dataIndex = context.dataIndex
              const originalData = data[dataIndex]
              if (!originalData) return []
              const brand = originalData.category[0] ?? ''

              const labels = [`${brand}:`]
              availableMetrics.forEach((metric, idx) => {
                const value = originalData.values[idx]
                if (value !== undefined) {
                  const formattedValue = metric.label
                    .toLowerCase()
                    .includes('volume')
                    ? value.toLocaleString()
                    : value.toFixed(2)
                  labels.push(`${metric.label}: ${formattedValue}`)
                }
              })

              return labels
            },
          },
        },
        datalabels: {
          anchor: 'end' as const,
          align: 'right' as const,
          // color: 'black',
          font: {
            size: 12,
          },
          formatter: (value: number) => {
            if (value === null || value === undefined) {
              return ALIAS_FOR_NULL_VALUES
            }
            const metric = availableMetrics.find((m) => m.key === primaryDomain)
            if (
              metric?.label.toLowerCase().includes('sentiment') ||
              metric?.label.toLowerCase().includes('score')
            ) {
              return value.toFixed(2)
            }
            return value.toLocaleString()
          },
          padding: {
            left: 8,
          },
        },
      },
      scales: {
        x: {
          beginAtZero:
            availableMetrics
              .find((m) => m.key === primaryDomain)
              ?.label.toLowerCase()
              .includes('volume') || false,
          max: availableMetrics
            .find((m) => m.key === primaryDomain)
            ?.label.toLowerCase()
            .includes('sentiment')
            ? 1
            : availableMetrics
                  .find((m) => m.key === primaryDomain)
                  ?.label.toLowerCase()
                  .includes('score')
              ? 5
              : Math.floor(maxValue * 1.1), // Set max to 1 for sentiment, otherwise use calculated max
          min: availableMetrics
            .find((m) => m.key === primaryDomain)
            ?.label.toLowerCase()
            .includes('sentiment')
            ? -1
            : availableMetrics
                  .find((m) => m.key === primaryDomain)
                  ?.label.toLowerCase()
                  .includes('score')
              ? 0
              : minValue < 0
                ? Math.floor(minValue * 1.1)
                : undefined, // Set min to -1 for sentiment
        },
        y: {
          ticks: {
            maxRotation: 0,
            font: {
              size: 11,
            },
          },
        },
      },
      layout: {
        padding: {
          right: 80, // Extra padding for data labels
        },
      },
    },
    plugins: [ChartDataLabels],
  }
}

export default configureBarChart
